(function(angular, _) {
    /**
     * @ngdoc service
     * @name PathGraphFactory
     * @description Represents the path graph model
     */
    class PathGraphFactory {
        static newVertex() {
            const PathVertexFactory = PathGraphFactory.$injector.get('PathVertexFactory');

            return new PathVertexFactory(...arguments);
        }

        static newEdge() {
            const PathEdgeFactory = PathGraphFactory.$injector.get('PathEdgeFactory');

            return new PathEdgeFactory(...arguments);
        }

        static newLevel() {
            const PathLevelFactory = PathGraphFactory.$injector.get('PathLevelFactory');

            return new PathLevelFactory(...arguments);
        }

        constructor(meta = {}) {
            this.meta = meta;
            this.vertices = new Set();
            this.edges = new Set();
            this.levels = new Map();
        }

        get verticesCount() {
            return this.vertices.size;
        }

        getMeta(path = null) {
            if (path) {
                return _.get(this.meta, path, null);
            }

            return this.meta;
        }

        setMeta(meta, value) {
            if (_.isPlainObject(meta)) {
                this.meta = meta;
            } else if (_.isUndefined(value)) {
                delete this.meta[meta];
            } else if (_.isString(meta)) {
                _.set(this.meta, meta, value);
            }

            return this;
        }

        // TODO Creates unnecessary work for the GC
        getVerticesList() {
            return Array.from(this.vertices);
        }

        getEdgesList() {
            return Array.from(this.edges);
        }

        getLevelsList() {
            return Array.from(this.levels.values());
        }

        isAdjacent(vertexA, vertexB) {
            return Boolean(this.getEdge(vertexA, vertexB) || this.getEdge(vertexB, vertexA));
        }

        getVertexChildren(vertex) {
            return _.map(vertex.getOutboundEdges(), 'head');
        }

        getVertexParents(vertex) {
            return _.map(vertex.getInboundEdges(), 'tail');
        }

        getVertexIncomingEdges(vertex) {
            if (!this.getVertex(vertex)) {
                return [];
            }

            return vertex.getInboundEdges();
        }

        getVertexOutgoingEdges(vertex) {
            if (!this.getVertex(vertex)) {
                return [];
            }

            return vertex.getOutboundEdges();
        }

        addVertex(vertex, level = null) {
            if (this.getVertex(vertex)) {
                return this.getVertex(vertex);
            }

            this.vertices.add(vertex);

            if (level) {
                level.addVertex(vertex);
                vertex.setLevel(level);
            }

            return vertex;
        }

        getVertex(vertex) {
            if (!this.vertices.has(vertex)) {
                return null;
            }

            return vertex;
        }

        getVertexValue(vertex) {
            if (!this.getVertex(vertex)) {
                return null;
            }

            return vertex.getValue();
        }

        setVertexValue(vertex, value) {
            if (this.getVertex(vertex)) {
                vertex.setValue(value);
            }

            return this;
        }

        removeVertex(vertex) {
            if (!this.getVertex(vertex)) {
                return this;
            }

            const parentVerticesList = this.getVertexParents(vertex);
            const childVerticesList = this.getVertexChildren(vertex);

            // Purge edges connected to this vertex
            _.forEach(parentVerticesList, (parentVertex) => (this.removeEdge(parentVertex, vertex)));
            _.forEach(childVerticesList, (childVertex) => (this.removeEdge(vertex, childVertex)));

            if (vertex.getLevel()) {
                vertex.getLevel().removeVertex(vertex);
                vertex.setLevel(null);
            }

            // Unregister and purge the vertex
            this.vertices.delete(vertex);
            vertex.destroy();

            return this;
        }

        addEdge(vertexA, vertexB, value = null, meta = {}) {
            if (!this.getVertex(vertexA) || !this.getVertex(vertexB)) {
                return null;
            }

            if (this.getEdge(vertexA, vertexB)) {
                return this.getEdge(vertexA, vertexB);
            }

            const edge = PathGraphFactory.newEdge(vertexA, vertexB, value, meta);

            this.edges.add(edge);

            vertexA.addOutboundEdge(edge);
            vertexB.addInboundEdge(edge);

            return edge;
        }

        getEdge(vertexA, vertexB) {
            if (!this.getVertex(vertexA) || !this.getVertex(vertexB)) {
                return null;
            }

            return _.find(vertexA.getOutboundEdges(), (outEdge) => (outEdge.head === vertexB));
        }

        getEdgeValue(vertexA, vertexB) {
            const edge = this.getEdge(vertexA, vertexB);

            return edge ? edge.getValue() : null;
        }

        setEdgeValue(vertexA, vertexB, value) {
            const edge = this.getEdge(vertexA, vertexB);

            if (edge) {
                edge.setValue(value);
            }

            return this;
        }

        removeEdge(vertexA, vertexB) {
            const edge = this.getEdge(vertexA, vertexB);

            if (edge) {
                vertexA.removeOutboundEdge(edge);
                vertexB.removeInboundEdge(edge);

                this.edges.delete(edge);
                edge.destroy();
            }

            return this;
        }

        graphTraverse(callback) {
            const register = new Set([]);

            _.forEach(this.getOriginVerticesList(), (vertex) => this.traverse(vertex, null, callback, register));

            return this;
        }

        traverse(toVertex, fromVertex = null, callback, register = new Set([])) {
            const edge = this.getEdge(fromVertex, toVertex);

            /* eslint callback-return: 0 */
            callback(toVertex, edge);

            // Traverse the sub-graph only if the node was not visited before (eg. via a different edge)
            if (!register.has(toVertex)) {
                register.add(toVertex);

                _.forEach(this.getVertexChildren(toVertex), (vertex) => this.traverse(vertex, toVertex, callback, register));
            }
        }

        getOriginVerticesList() {
            const originVertices = [];

            for (const vertex of this.vertices) {
                if (!vertex.hasInboundEdges) {
                    originVertices.push(vertex);
                }
            }

            return originVertices;
        }

        getTerminalVerticesList() {
            const terminalVertices = [];

            for (const vertex of this.vertices) {
                if (!vertex.hasOutboundEdges) {
                    terminalVertices.push(vertex);
                }
            }

            return terminalVertices;
        }

        addLevel(level) {
            this.levels.set(level.getIdentifier(), level);

            return this;
        }

        getLevel(identifier) {
            return this.levels.get(identifier);
        }

        removeLevel(level) {
            this.levels.delete(level.getIdentifier());

            return this;
        }

        toJSON() {
            const output = [];

            this.graphTraverse((vertex, inEdge) => {
                output.push({
                    vertex: vertex.toJSON(),
                    inEdge: inEdge ? inEdge.toJSON() : null
                });
            });

            return output;
        }

        destroy() {
            this.graphTraverse((vertex, inEdge) => {
                vertex.destroy();

                if (inEdge) {
                    inEdge.destroy();
                }
            });

            this.meta = null;
            this.adjacencyMatrix = null;
            this.vertices = null;
            this.edges = null;
            this.levels = null;
        }
    }

    angular.module('wtApp.dataService')
        .factory('PathGraphFactory', ['$injector', ($injector) => {
            PathGraphFactory.$injector = $injector;

            return PathGraphFactory;
        }]);
})(window.angular, window._);
